import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { uploadReservationImage } from '../../../Redux/ReservationSettings/reservationSettingsApi';
import { ReservationImageKind } from '../../../types/reservation';

interface Props {
  storeId: string;
  /** presign の kind（COURSE=コース画像 / STORE=店舗写真） */
  kind: ReservationImageKind;
  /** 現在の画像 URL リスト（並び順=表示順） */
  urls: string[];
  onChange: (urls: string[]) => void;
  /** 最大枚数（コース=3・ギャラリー=6・ヒーロー=1） */
  maxCount: number;
  /** 追加ボタンのラベル */
  addLabel?: string;
  disabled?: boolean;
  onError: (e: unknown) => void;
}

/**
 * 予約画像（コース画像・店舗写真）の共通アップローダー。
 * 既存メニュー画像アップローダの UX（ファイル選択→リサイズ→presigned PUT）を踏襲し、
 * 選択と同時に S3 へアップロードして URL リストを onChange で返す。
 * URL の確定（コース保存 / reservation-store-images PUT）は呼び出し側の保存操作で行う。
 * maxCount > 1 のときは ◀▶ ボタンで並び替えできる。
 */
const ReservationImageUploader: React.FC<Props> = ({
  storeId,
  kind,
  urls,
  onChange,
  maxCount,
  addLabel = '画像を追加',
  disabled = false,
  onError,
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 同じファイルを続けて選択できるよう毎回リセットする
    e.target.value = '';
    if (!file || urls.length >= maxCount) return;
    setUploading(true);
    try {
      const publicUrl = await uploadReservationImage(storeId, file, kind);
      onChange([...urls, publicUrl]);
    } catch (error) {
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  const remove = (index: number) => {
    onChange(urls.filter((_, i) => i !== index));
  };

  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= urls.length) return;
    const next = [...urls];
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: urls.length > 0 ? 1 : 0 }}>
        {urls.map((url, index) => (
          <Box key={url} sx={{ width: 96 }}>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={url}
                alt={`画像${index + 1}`}
                sx={{
                  width: 96,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 1,
                  display: 'block',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <IconButton
                size="small"
                onClick={() => remove(index)}
                disabled={disabled || uploading}
                aria-label="画像を削除"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'grey.100' },
                }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            {maxCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => move(index, -1)}
                  disabled={disabled || uploading || index === 0}
                  aria-label="前へ移動"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => move(index, 1)}
                  disabled={disabled || uploading || index === urls.length - 1}
                  aria-label="後ろへ移動"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />
      <Button
        size="small"
        startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateOutlinedIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading || urls.length >= maxCount}
      >
        {uploading ? 'アップロード中...' : addLabel}
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {`${urls.length}/${maxCount}枚`}
      </Typography>
    </Box>
  );
};

export default ReservationImageUploader;
